/**
 * Pure helpers for the People tab: how a person's row reads, and how an access
 * explanation is turned into the chain operations sees. No React, no I/O, so
 * every rule the screen states can be tested on its own.
 */

/**
 * LendingPad shows "System Administrator"; its API returns
 * "SystemAdministrator". Operations reads both screens, so they should match.
 *
 * @param {string} roleType
 * @returns {string}
 */
export function humanizeRoleType(roleType) {
  if (typeof roleType !== "string") {
    return "";
  }

  return roleType.replace(/([a-z0-9])([A-Z])/g, "$1 $2").trim();
}

export function subjectKey(subject) {
  return subject.subjectType === "owner" ? "owner" : `member:${subject.memberId}`;
}

/** Rows operations can do something about, and should look at first. */
export function needsAttention(subject) {
  const { capture } = subject;

  return (
    !capture.present || capture.isStale || capture.ownerUidMatches === false
  );
}

/**
 * What the person actually opens on the loan board, after every rule has had
 * its say — the answer the row leads with. LendingPad's verdict comes from the
 * server; the Addy role can only narrow it, and decides alone when LendingPad
 * has no reading.
 *
 * @param {Object} subject
 * @returns {"all"|"own"}
 */
export function effectiveAccess(subject) {
  const visibility = subject.resolution?.visibility;
  if (visibility === "own_loans") {
    return "own";
  }
  if (visibility === "all_loans") {
    return subject.addyViewScope === "own" ? "own" : "all";
  }

  return subject.addyViewScope === "all" ? "all" : "own";
}

/**
 * Why the row reads the way it does, said the way operations would say it.
 * `act` marks the reasons someone can do something about.
 *
 * @param {Object} subject
 * @returns {{ text: string, act: boolean }}
 */
export function accessReason(subject) {
  const source = subject.resolution?.source;
  const narrowedByAddy =
    subject.resolution?.visibility === "all_loans" &&
    subject.addyViewScope === "own";

  if (source === "ops_selection") {
    return {
      text: narrowedByAddy
        ? `Set by the ops team, but their Addy role (${subject.addyRole}) still limits them to loans they are on.`
        : "Set by the ops team.",
      act: narrowedByAddy,
    };
  }
  if (source === "captured") {
    return {
      text: narrowedByAddy
        ? `From their LendingPad roles, narrowed by their Addy role (${subject.addyRole}).`
        : "From their LendingPad roles.",
      act: false,
    };
  }
  if (source === "stale_capture") {
    return {
      text: "LendingPad reading is over 30 days old. Their Addy role decides for now.",
      act: true,
    };
  }
  if (source === "owner_uid_mismatch") {
    return {
      text: "These roles belong to a previous owner. Forget them to start over.",
      act: true,
    };
  }

  return {
    text: "LendingPad roles not read yet. Their Addy role decides for now.",
    act: true,
  };
}

/**
 * Turn the server's access explanation into the chain the inspector renders,
 * with one sentence on what to do about it.
 *
 * @param {Object} explanation - Payload of GET …/explain-access.
 * @returns {{ steps: Array<{ tone: string, label: string }>, verdict: string, fix: string }}
 */
export function describeExplanation(explanation) {
  if (!explanation) {
    return { steps: [], verdict: "", fix: "" };
  }

  if (!explanation.found) {
    const last = explanation.lastSync;
    const steps = [
      { tone: "no", label: "Not in Addy" },
      last
        ? {
            tone: last.status === "skipped" ? "warn" : "off",
            label: last.reason
              ? `Last sync: ${last.status} (${last.reason})`
              : `Last sync: ${last.status || "unknown"}`,
          }
        : { tone: "off", label: "No sync recorded for it" },
    ];
    const fix = last?.reason
      ? /team member/i.test(last.reason)
        ? "Nobody from this organization is on the loan's LendingPad team. Add the originator as a LendingPad-only person, or switch Import policy to every loan."
        : /excluded from sync/i.test(last.reason)
          ? "Its stage is not imported. Enable the stage in Import policy, or wait for the loan to move."
          : "Check the sync reason above, then run Sync Data or Data Check."
      : "It was never delivered or imported. Run Data Check to see whether LendingPad still holds it.";

    return { steps, verdict: "No. It is not in Addy.", fix };
  }

  const { person, loan, canOpen } = explanation;
  const visibility = person.resolution?.visibility;
  const lendingPadStep =
    person.opsOutcome && person.opsOutcome !== "follow"
      ? {
          tone: "ok",
          label:
            person.opsOutcome === "all"
              ? "Ops decided: every loan"
              : "Ops decided: only their own",
        }
      : visibility === "all_loans"
        ? { tone: "ok", label: "LendingPad role: admin" }
        : visibility === "own_loans"
          ? { tone: "warn", label: "LendingPad role: scoped" }
          : { tone: "off", label: "LendingPad: no reading" };
  const addyStep =
    person.addyViewScope === "own"
      ? { tone: "warn", label: `Addy role: ${person.addyRole} (own loans only)` }
      : { tone: "ok", label: `Addy role: ${person.addyRole || "none"}` };
  const opensAll = effectiveAccess(person) === "all";
  // A narrowing step is only a warning when it blocked something. Once the
  // verdict is yes, those steps are facts, not alarms.
  const settle = (step) =>
    canOpen === true && step.tone === "warn" ? { ...step, tone: "" } : step;
  const steps = [
    {
      tone: "ok",
      label: loan.los === "lendingpad" ? "In Addy, from LendingPad" : "In Addy",
    },
    person.onLoan
      ? {
          tone: "ok",
          label:
            person.onLoanAs && person.onLoanAs !== person.email
              ? `On this loan's team as ${person.onLoanAs}`
              : "On this loan's team",
        }
      : { tone: "warn", label: "Not on this loan's team" },
    lendingPadStep,
    addyStep,
  ].map(settle);

  let verdict = "The board's verdict could not be computed.";
  if (canOpen === true) {
    verdict = "Yes, they can open it.";
  } else if (canOpen === false) {
    verdict = "No, they cannot open it.";
  }

  // The loan names people, but none of those addresses is this person's login
  // or linked to it. Either they are not on the loan, or they are on it under
  // an address Addy does not know is theirs; only an operator can tell which.
  const unlinkedTeam =
    !person.onLoan && !opensAll && (loan.teamEmails || []).length > 0
      ? loan.teamEmails
      : null;

  let fix = "";
  if (canOpen === false) {
    fix = opensAll
      ? "Their access is not limited by LendingPad or their Addy role, so something else applies to this loan. Check its organization and archive state."
      : unlinkedTeam
        ? `The loan's team is ${unlinkedTeam.join(", ")}. If one of those addresses is this person, link it under Why? in the table above. Otherwise add them to the loan's team in LendingPad${
            person.addyViewScope === "own" ? "" : ", or set them to “Every imported loan”"
          }.`
        : person.addyViewScope === "own"
          ? `Their Addy role (${person.addyRole}) limits them to loans they are on. Add them to the loan's team in LendingPad, or change their Addy role on the team page.`
          : "Set them to “Every imported loan” in the table above, or add them to the loan's team in LendingPad.";
  }

  return { steps, verdict, fix };
}
